import { ReactNode, useCallback, useEffect, useState } from "react"
import FingerprintJS from "@fingerprintjs/fingerprintjs"
import { Text } from "../../../components"
import { API_BASE_URL, SHOW_MAPS } from '../../../config'

const STARS = [1, 2, 3, 4, 5]

interface RatingData {
    raiting: number;
    count: number;
}

let fingerprintPromise: Promise<string> | null = null

const getFingerprint = () => {
    fingerprintPromise ??= FingerprintJS.load({monitoring: false})
        .then((fp) => fp.get())
        .then(({visitorId}) => visitorId)
    fingerprintPromise.catch(() => { fingerprintPromise = null })
    return fingerprintPromise
}

const requestPosition = (options: PositionOptions) => new Promise<GeolocationPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options)
})

const getLocation = async () => {
    if (!navigator.geolocation) throw new Error('Геолокация не поддерживается')
    let position: GeolocationPosition
    try {
        position = await requestPosition({enableHighAccuracy: true, timeout: 10000, maximumAge: 60000})
    } catch (err) {
        // GPS в помещении часто не успевает — пробуем по сети/Wi-Fi
        if ((err as GeolocationPositionError).code === 1) throw err
        position = await requestPosition({enableHighAccuracy: false, timeout: 15000, maximumAge: 300000})
    }
    const {latitude, longitude} = position.coords
    return `${latitude},${longitude}`
}

const locationErrorMessage = (err: unknown) => {
    const code = (err as GeolocationPositionError | undefined)?.code
    if (code === 1) return 'Разрешите доступ к геолокации, чтобы оценить заведение'
    if (code === 2 || code === 3) return 'Не удалось определить местоположение, попробуйте ещё раз'
    return 'Геолокация недоступна в этом браузере'
}

const pluralRatings = (count: number) => {
    const mod10 = count % 10
    const mod100 = count % 100
    if (mod10 === 1 && mod100 !== 11) return 'оценка'
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'оценки'
    return 'оценок'
}

const StarIcon = () => (
    <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
        <path d="M12 2.5l2.94 5.96 6.56.95-4.75 4.63 1.12 6.54L12 17.5l-5.87 3.08 1.12-6.54L2.5 9.41l6.56-.95z" />
    </svg>
)

interface RatingProps {
    storeId: number;
    // Доп. ссылка в строке под звёздами (например, «На карте»)
    action?: ReactNode;
    // Оценка успешно отправлена — например, для начисления баллов
    onRated?: () => void;
    // Баллы за оценку уже получены — показываем отметку
    rewardedPoints?: number;
}

export const Rating = ({storeId, action, onRated, rewardedPoints}: RatingProps) => {
    const [data, setData] = useState<RatingData | null>(null)
    const [hover, setHover] = useState<number | null>(null)
    const [sending, setSending] = useState(false)
    const [message, setMessage] = useState<string | null>(null)

    const loadRating = useCallback(() => {
        fetch(`${API_BASE_URL}/get-raiting?store_id=${storeId}`)
            .then((res) => res.ok ? res.json() : null)
            .then((json: RatingData | null) => setData(json))
            .catch(() => setData(null))
    }, [storeId])

    useEffect(() => { loadRating() }, [loadRating])

    // Фингерпринт снимаем сразу при открытии: если считать его после запроса геолокации,
    // окно разрешения влияет на компоненты и в инкогнито получается другой ID
    useEffect(() => { getFingerprint().catch(() => {}) }, [])

    const sendRating = async (value: number) => {
        if (sending) return
        setSending(true)
        setMessage('Определяем ваше местоположение…')
        try {
            const fingerprintRequest = getFingerprint()
            let location: string
            try {
                location = await getLocation()
            } catch (err) {
                const {code, message} = (err ?? {}) as Partial<GeolocationPositionError>
                console.warn('[rating] не удалось получить координаты:', code, message)
                setMessage(locationErrorMessage(err))
                return
            }
            const fingerprint = await fingerprintRequest
            const params = new URLSearchParams({
                store_id: String(storeId),
                fingerprint,
                location,
                raiting: String(value),
            })
            const res = await fetch(`${API_BASE_URL}/post-raiting?${params}`, {method: 'POST'})
            if (res.status === 403) {
                setMessage('Оценить можно только находясь в заведении')
                return
            }
            if (!res.ok) throw new Error('request failed')
            setMessage('Спасибо за оценку!')
            onRated?.()
            loadRating()
        } catch {
            setMessage('Не удалось отправить оценку, попробуйте ещё раз')
        } finally {
            setSending(false)
        }
    }

    if (!SHOW_MAPS) {
        return null
    }

    // При наведении показываем выбираемую оценку, иначе — среднюю
    const shown = hover ?? data?.raiting ?? 0

    return (
        <div className="get-qr-rating">
            <div className="get-qr-stars" onMouseLeave={() => setHover(null)}>
                {STARS.map((star) => {
                    const fill = Math.min(Math.max(shown - star + 1, 0), 1)
                    return (
                        <button
                            key={star}
                            type="button"
                            className="get-qr-star"
                            aria-label={`Оценить на ${star}`}
                            disabled={sending}
                            onMouseEnter={() => setHover(star)}
                            onClick={() => sendRating(star)}
                        >
                            <span className="get-qr-star-empty"><StarIcon /></span>
                            <span className="get-qr-star-fill" style={{width: `${fill * 100}%`}}><StarIcon /></span>
                        </button>
                    )
                })}
            </div>
            <div className="get-qr-rating-caption">
                <Text size="xs" color="lightGray">
                    {message ?? (data
                        // ? `${data.raiting.toFixed(1)} · ${data.count} ${pluralRatings(data.count)}`
                        ? `${data.raiting.toFixed(1)}`
                        : 'Пока мало оценок - собираем статистику')}
                </Text>
                {rewardedPoints !== undefined && (
                    <span className="link-points" title="Баллы за оценку получены">
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M2.5 6.5l2.5 2.5 4.5-5" />
                        </svg>
                        +{rewardedPoints}
                    </span>
                )}
                {action && !message && <>
                    <span className="get-qr-rating-dot">·</span>
                    {action}
                </>}
            </div>
        </div>
    )
}
